import {
  type OrgStaffMember,
  organizationStaffQueryKey,
  useOrganizationStaffQuery,
  useResetOrganizationReferralContactsMutation,
  useUpdateSchoolReferralContactMutation,
  useUpdateUserPhoneNumberMutation,
} from '@cartwheel/api/v1'
import {
  Button,
  Modal,
  type ReferralContact,
  SchoolReferralContactPicker,
} from '@cartwheel/components'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

interface School {
  id: string
  name: string
  studentCount: number
  pointOfContact: OrgStaffMember | null
}

interface Props {
  organizationId: string
  schools: School[]
}

// The API and Rails props both use the snake_case OrgStaffMember shape; the picker
// wants camelCase ReferralContact.
function mapContact(member: OrgStaffMember): ReferralContact {
  return {
    id: member.id,
    name: member.name,
    title: member.title,
    avatarUrl: member.avatar_url,
    phoneNumber: member.phone_number,
  }
}

export default function SchoolReferralContactsTable({
  organizationId,
  schools: initialSchools,
}: Props) {
  const queryClient = useQueryClient()
  const [schools, setSchools] = useState<School[]>(initialSchools)
  const [confirmingReset, setConfirmingReset] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useOrganizationStaffQuery({
    path: { organization_id: organizationId },
  })
  const staff = useMemo(() => (data?.staff ?? []).map(mapContact), [data])
  const districtDefaults = useMemo(
    () => (data?.district_default_contacts ?? []).map(mapContact),
    [data]
  )

  const assignMutation = useUpdateSchoolReferralContactMutation()
  const phoneMutation = useUpdateUserPhoneNumberMutation()
  const resetMutation = useResetOrganizationReferralContactsMutation()

  const refreshStaff = () =>
    queryClient.invalidateQueries({
      queryKey: organizationStaffQueryKey({ path: { organization_id: organizationId } }),
    })

  async function handleSelect(schoolId: string, userId: string | null) {
    setError(null)
    try {
      await assignMutation.mutateAsync({
        path: { organization_id: organizationId, id: schoolId },
        body: { institution: { point_of_contact_id: userId } },
      })
      const contact = userId ? (data?.staff.find((m) => m.id === userId) ?? null) : null
      setSchools((prev) =>
        prev.map((s) => (s.id === schoolId ? { ...s, pointOfContact: contact } : s))
      )
    } catch {
      setError("Couldn't update the referral contact. Please try again.")
    }
  }

  async function handleSavePhone(userId: string, digits: string) {
    setError(null)
    try {
      await phoneMutation.mutateAsync({
        path: { id: userId },
        body: { user: { phone_number: digits } },
      })
      // Reflect the new phone on every school this person is the contact for.
      setSchools((prev) =>
        prev.map((s) =>
          s.pointOfContact && s.pointOfContact.id === userId
            ? { ...s, pointOfContact: { ...s.pointOfContact, phone_number: digits } }
            : s
        )
      )
      refreshStaff()
    } catch {
      setError("Couldn't save the phone number. Please try again.")
    }
  }

  async function handleResetAll() {
    setError(null)
    try {
      await resetMutation.mutateAsync({ path: { organization_id: organizationId } })
      setSchools((prev) => prev.map((s) => ({ ...s, pointOfContact: null })))
    } catch {
      setError("Couldn't reset the contacts. Please try again.")
    } finally {
      setConfirmingReset(false)
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-row items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium text-charcoal">
            Auto-approve referral contact by school
          </h3>
          <p className="text-sm text-charcoal-50">
            One staff member is added to every auto-approved referral. Set a contact per school, or
            leave it on the district MPOC.
          </p>
        </div>
        {schools.length > 0 && (
          <Button
            variant="secondary"
            onClick={() => setConfirmingReset(true)}
            disabled={resetMutation.isPending}
          >
            Reset all to MPOC
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-brick-light">{error}</p>}

      {schools.length === 0 ? (
        <p className="text-sm text-charcoal-50">
          No schools yet — add a school to configure its referral contact.
        </p>
      ) : (
        <div className="divide-y divide-gray-stroke rounded-lg border border-gray-stroke">
          {schools.map((school) => (
            <div
              key={school.id}
              className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="sm:w-1/3">
                <p className="font-medium text-charcoal">{school.name}</p>
                <p className="text-xs text-charcoal-50">
                  {school.studentCount.toLocaleString()} students
                </p>
              </div>
              <div className="sm:flex-1">
                <SchoolReferralContactPicker
                  value={school.pointOfContact ? mapContact(school.pointOfContact) : null}
                  staff={staff}
                  districtDefaultContacts={districtDefaults}
                  onSelect={(userId) => handleSelect(school.id, userId)}
                  onSavePhone={handleSavePhone}
                  isSaving={assignMutation.isPending || phoneMutation.isPending}
                  disabled={isLoading}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={confirmingReset}
        onClose={() => setConfirmingReset(false)}
        title="Reset all to district MPOC?"
        footer={
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setConfirmingReset(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetAll} disabled={resetMutation.isPending}>
              Reset all
            </Button>
          </div>
        }
      >
        <p className="text-charcoal">
          Are you sure? This will replace any school-specific contacts you&apos;ve set.
        </p>
      </Modal>
    </section>
  )
}
